import React, { useState, useEffect } from 'react';
import '../mobile-responsive.css';

const EmailTest = () => {
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    subject: 'Test Email from Cixio Marketplace',
    message: 'Hello! This is a test email to verify the email system is working correctly.'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  // Load email configuration on mount
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/email-test/config');
      const data = await response.json();
      setConfig(data.config);
      
      // Set default values from config
      if (data.config) {
        setFormData(prev => ({
          ...prev,
          from: data.config.from_email || '',
          to: data.config.test_email || ''
        }));
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await fetch('http://localhost:5000/api/email-test/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success && data.result.success) {
        setResult({
          success: true,
          message: 'Email sent successfully! ✅',
          messageId: data.result.messageId,
          to: formData.to
        });
      } else {
        setError(data.result?.error || data.message || 'Failed to send email');
      }
    } catch (err) {
      setError('Failed to connect to server. Make sure the server is running on port 5000.');
    } finally {
      setLoading(false);
    }
  };

  const testInvoice = async () => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await fetch('http://localhost:5000/api/email-test/invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: formData.to })
      });

      const data = await response.json();
      
      if (data.success && data.result.success) {
        setResult({
          success: true,
          message: 'Invoice email sent successfully! ✅',
          messageId: data.result.messageId,
          to: formData.to
        });
      } else {
        setError(data.result?.error || 'Failed to send invoice email');
      }
    } catch (err) {
      setError('Failed to send invoice email');
    } finally {
      setLoading(false);
    }
  };

  const testPayment = async () => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await fetch('http://localhost:5000/api/email-test/payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: formData.to })
      });

      const data = await response.json();
      
      if (data.success && data.result.success) {
        setResult({
          success: true,
          message: 'Payment receipt sent successfully! ✅',
          messageId: data.result.messageId,
          to: formData.to
        });
      } else {
        setError(data.result?.error || 'Failed to send payment receipt');
      }
    } catch (err) {
      setError('Failed to send payment receipt');
    } finally {
      setLoading(false);
    }
  };

  const testAll = async () => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await fetch('http://localhost:5000/api/email-test/all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: formData.to })
      });

      const data = await response.json();
      
      if (data.success) {
        const successCount = Object.values(data.results).filter(r => r?.success).length;
        setResult({
          success: true,
          message: `Sent ${successCount}/3 test emails successfully! ✅`,
          details: data.results,
          to: formData.to
        });
      } else {
        setError('Failed to send test emails');
      }
    } catch (err) {
      setError('Failed to send test emails');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        padding: '30px',
        borderRadius: '10px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '32px' }}>📧 Email Testing</h1>
        <p style={{ margin: 0, opacity: 0.9 }}>Test email functionality with AWS SES</p>
      </div>

      {/* Configuration Status */}
      {config && (
        <div style={{
          background: config.configured ? '#ecfdf5' : '#fef3c7',
          border: `2px solid ${config.configured ? '#10b981' : '#f59e0b'}`,
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>
            {config.configured ? '✅ Email Service Ready' : '⚠️ Email Service Not Configured'}
          </h3>
          <div style={{ color: '#555', fontSize: '14px' }}>
            <p style={{ margin: '5px 0' }}>
              The email service is {config.configured ? 'properly configured and ready to send emails' : 'not configured yet'}.
            </p>
            {config.configured && (
              <p style={{ margin: '5px 0', color: '#059669' }}>
                ✓ SMTP credentials are configured securely on the server
              </p>
            )}
          </div>
        </div>
      )}

      {/* Email Form */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginTop: 0, color: '#333' }}>Send Test Email</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555' }}>
              From Email Address *
            </label>
            <input
              type="email"
              name="from"
              value={formData.from}
              onChange={handleChange}
              required
              placeholder="noreply@cixio.ai"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <small style={{ color: '#6b7280', fontSize: '14px', marginTop: '5px', display: 'block' }}>
              Default from .env: FROM_EMAIL
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555' }}>
              To Email Address *
            </label>
            <input
              type="email"
              name="to"
              value={formData.to}
              onChange={handleChange}
              required
              placeholder="recipient@example.com"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <small style={{ color: '#6b7280', fontSize: '14px', marginTop: '5px', display: 'block' }}>
              Default from .env: TEST_EMAIL
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555' }}>
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Email subject"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555' }}>
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="5"
              placeholder="Email message"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              background: loading ? '#94a3b8' : '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.3s'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.background = '#5568d3')}
            onMouseLeave={(e) => !loading && (e.target.style.background = '#6366f1')}
          >
            {loading ? '⏳ Sending...' : '📤 Send Test Email'}
          </button>
        </form>
      </div>

      {/* Template Testing Buttons */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginTop: 0, color: '#333' }}>Test Email Templates</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Test pre-built email templates (requires "To" email address above)
        </p>

        <div style={{ display: 'grid', gap: '15px' }}>
          <button
            onClick={testInvoice}
            disabled={loading || !formData.to}
            style={{
              padding: '15px',
              background: loading || !formData.to ? '#94a3b8' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading || !formData.to ? 'not-allowed' : 'pointer'
            }}
          >
            📄 Test Invoice Email
          </button>

          <button
            onClick={testPayment}
            disabled={loading || !formData.to}
            style={{
              padding: '15px',
              background: loading || !formData.to ? '#94a3b8' : '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading || !formData.to ? 'not-allowed' : 'pointer'
            }}
          >
            💳 Test Payment Receipt
          </button>

          <button
            onClick={testAll}
            disabled={loading || !formData.to}
            style={{
              padding: '15px',
              background: loading || !formData.to ? '#94a3b8' : '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading || !formData.to ? 'not-allowed' : 'pointer'
            }}
          >
            🚀 Test All Templates (3 emails)
          </button>
        </div>
      </div>

      {/* Success Message */}
      {result && (
        <div style={{
          background: '#ecfdf5',
          border: '2px solid #10b981',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#059669' }}>
            {result.message}
          </h3>
          <div style={{ color: '#065f46' }}>
            <p><strong>Sent to:</strong> {result.to}</p>
            {result.messageId && (
              <p><strong>Message ID:</strong> <code>{result.messageId}</code></p>
            )}
            {result.details && (
              <div style={{ marginTop: '15px' }}>
                <strong>Details:</strong>
                <pre style={{ 
                  background: '#d1fae5', 
                  padding: '10px', 
                  borderRadius: '5px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(result.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '2px solid #ef4444',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#dc2626' }}>
            ❌ Error
          </h3>
          <p style={{ color: '#991b1b', margin: 0 }}>
            {error}
          </p>
        </div>
      )}

      {/* Instructions */}
      <div style={{
        background: '#f3f4f6',
        borderRadius: '10px',
        padding: '20px',
        color: '#374151'
      }}>
        <h3 style={{ marginTop: 0 }}>📝 Instructions</h3>
        <ul style={{ marginBottom: 0 }}>
          <li>Enter the recipient email address in the "To" field</li>
          <li>Click "Send Test Email" to send a custom email</li>
          <li>Or use the template buttons to test pre-built email templates</li>
          <li>Check the recipient's inbox for the email</li>
          <li>Make sure the server is running on port 5000</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailTest;
